import { HStack, Pressable, Text } from '@ayble-health/dashboard-components';

type SegmentOption<T extends string> = {
  value: T;
  label: string;
};

type Props<T extends string> = {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
};

/**
 * A pill-style exclusive choice, matching the mockup's shared Billing Type /
 * Lab Vendor control.
 *
 * @example
 * <SegmentedControl options={VENDOR_OPTIONS} value={vendor} onChange={setVendor} />
 */
export const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: Props<T>) => (
  <HStack
    gap={1}
    p={1}
    w={'100%'}
    bg={'feather'}
    rounded={'full'}
    role={'radiogroup'}
    aria-label={ariaLabel}
  >
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <Pressable
          key={option.value}
          onClick={() => onChange(option.value)}
          role={'radio'}
          aria-checked={isActive}
          flex={1}
          py={2}
          px={4}
          textAlign={'center'}
          rounded={'full'}
          bg={isActive ? 'white' : 'transparent'}
          boxShadow={isActive ? 'sm' : 'none'}
        >
          <Text
            fontSize={'sm'}
            fontWeight={600}
            textAlign={'center'}
            color={isActive ? 'seafoam' : 'bluegrey'}
          >
            {option.label}
          </Text>
        </Pressable>
      );
    })}
  </HStack>
);

export default SegmentedControl;
